import { useDateFormatter } from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Calendar } from '@mint-lib/icons';
import { Card, Chip, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { getRouteApi } from '@mint/routing';
import { parseISO } from 'date-fns';

import { useChosenVersionId } from '../../../../../../hooks/useChosenVersionId.js';

type VersionCardProps = {
  id: string | null;
  name: string;
  createdAt: string | null;
  isCurrent: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

const routerApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const VersionCard = ({
  id,
  name,
  createdAt,
  isCurrent,
  onOpenChange,
}: VersionCardProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const navigate = routerApi.useNavigate();

  const chosenVersionId = useChosenVersionId();

  const isDisabled = isCurrent ? !chosenVersionId : id === chosenVersionId;

  const handleSelectVersion = () => {
    if (isDisabled) return;

    navigate({
      search: ({ flightVersionId: _removed, ...updatedSearch }) =>
        // Current version has no version param; archived versions are
        // addressed by their id.
        isCurrent
          ? updatedSearch
          : { ...updatedSearch, flightVersionId: id ?? undefined },
    });
    onOpenChange(false);
  };

  const dateFormatter = useDateFormatter();
  const formattedDate = createdAt
    ? dateFormatter.formatDate(parseISO(createdAt))
    : null;

  return (
    <Card
      className="my-2 w-full"
      contentClassName={cn('flex justify-between', {
        'cursor-pointer hover:bg-ui-secondary': !isDisabled,
        'cursor-default': isDisabled,
      })}
      onClick={handleSelectVersion}
      disabled={isDisabled}
    >
      <div className="flex-col">
        <p
          className={cn(
            typographyVariants({
              variant: 'bodyShort01',
              className: 'pb-3',
            }),
          )}
        >
          {name}
        </p>
        <Chip variant="outline" color="silver">
          <Calendar />
          {t('Created: {{formattedDate}}', {
            formattedDate,
          })}
        </Chip>
      </div>
    </Card>
  );
};
